import React, { useEffect, useRef } from 'react';
import ResponsavelTransparencia from '../components/ResponsavelTransparencia';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { transparenciaService } from '../services/transparenciaService';
import { emendaClassificationLabel, emendaNumeroExercicio } from '../utils/emendaFormatters';
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatEnum,
  formatFuncaoGoverno,
  formatPercent,
  numeroComAno,
  usePortalTitle,
} from '../utils/transparenciaFormat';
import { PortalAcessibilidadeShell, SituacaoBadge, usePortalAcessibilidade } from '../components/PortalAcessibilidade';

const ATO_ORCAMENTARIO_LABEL: Record<string, string> = {
  LOA: 'Lei Orçamentária Anual (LOA)',
  CREDITO_ADICIONAL: 'Crédito adicional',
};

const PRESTACAO_SITUACAO_LABEL: Record<string, string> = {
  NAO_INICIADA: 'Não iniciada',
  EM_ANDAMENTO: 'Em andamento',
  CONCLUIDA: 'Concluída',
};

const PRESTACAO_PC_STATUS_LABEL: Record<string, string> = {
  ABERTA: 'Aberta',
  FECHADA: 'Encerrada',
  AJUSTE_SOLICITADO: 'Ajuste solicitado',
};

const isPlanoAprovado = (tipo?: string): boolean => [
  'PLANO_ASSINADO_PRESIDENTE',
  'PLANO_TRABALHO_ASSINADO_PREFEITO',
].includes(tipo ?? '');

const TransparenciaEmendaPage: React.FC = () => {
  const { tema, barra } = usePortalAcessibilidade();
  const { officialCode = '' } = useParams<{ officialCode: string }>();
  const { data: emenda, isLoading, isError, error: emendaError, refetch } = useQuery({
    queryKey: ['transparencia-emenda', officialCode],
    queryFn: () => transparenciaService.getByOfficialCode(officialCode),
    enabled: Boolean(officialCode),
    retry: false,
  });
  const { data: tiposEmenda = [] } = useQuery({
    queryKey: ['transparencia-emendas-tipos'],
    queryFn: transparenciaService.tipos,
    staleTime: 30 * 60 * 1000,
  });
  const { data: metadata, isError: metadataIndisponivel } = useQuery({
    queryKey: ['transparencia-emendas-metadados'],
    queryFn: transparenciaService.metadata,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const identificacao = emenda ? emendaNumeroExercicio(emenda.numeroEmenda, emenda.exercicio, emenda.officialCode) : null;
  usePortalTitle(identificacao ? `Emenda ${identificacao.numero}/${identificacao.exercicio}` : `Emenda ${officialCode}`);

  useEffect(() => {
    if (emenda) titleRef.current?.focus();
  }, [emenda]);

  const card = `scroll-mt-4 rounded-xl p-5 ${tema.card}`;
  const dt = `text-xs font-semibold uppercase ${tema.label}`;
  const vazio = `mt-4 rounded-lg border border-dashed p-4 text-sm ${tema.divider} ${tema.muted}`;
  const th = 'px-3 py-2 text-left text-xs font-semibold uppercase';
  const botaoSecundario = `rounded-md border px-4 py-2 text-sm font-semibold ${tema.secondary}`;
  const botaoPrimario = `rounded-md px-4 py-2 text-sm font-semibold ${tema.primary}`;

  if (isLoading) {
    return (
      <PortalAcessibilidadeShell tema={tema} barra={barra}>
        <main id="conteudo-principal" aria-live="polite" className="flex-1 px-4 py-16 text-center">Carregando emenda...</main>
      </PortalAcessibilidadeShell>
    );
  }

  if (isError || !emenda || !identificacao) {
    const status = (emendaError as { response?: { status?: number } } | null)?.response?.status;
    const indisponivel = isError && status !== 404;
    return (
      <PortalAcessibilidadeShell tema={tema} barra={barra}>
        <main id="conteudo-principal" className="flex-1 px-4 py-16 text-center">
          <h1 className="text-2xl font-bold">{indisponivel ? 'Consulta temporariamente indisponível' : 'Emenda não encontrada'}</h1>
          <p className={`mt-2 text-sm ${tema.muted}`}>{indisponivel ? 'O serviço público não respondeu. Tente novamente em alguns instantes.' : `Não há emenda publicada com o código ${officialCode}.`}</p>
          <div className="mt-6 flex justify-center gap-3">
            <Link to="/transparencia" className={botaoSecundario}>Voltar à consulta</Link>
            {indisponivel && <button type="button" onClick={() => refetch()} className={`inline-flex items-center gap-2 ${botaoPrimario}`}><ArrowPathIcon className="h-4 w-4" aria-hidden="true" />Tentar novamente</button>}
          </div>
        </main>
      </PortalAcessibilidadeShell>
    );
  }

  const instrumento = emenda.numeroConvenio
    ? numeroComAno(emenda.numeroConvenio, emenda.anoConvenio)
    : formatEnum(emenda.modalidadeInstrumento);
  const movimentos = emenda.execucaoFinanceira ?? [];
  const plano = emenda.planoTrabalho;
  const documentos = emenda.documentos ?? [];
  const evidencias = emenda.evidencias ?? [];
  const despesas = emenda.despesas ?? [];
  const prestacaoContas = emenda.prestacaoContas;
  const execucaoPorMeta = emenda.execucaoPorMeta ?? [];
  const alcanceMetas = emenda.alcanceMetas ?? [];
  const bens = emenda.bens ?? [];
  const anexosPrestacao = emenda.anexosPrestacao ?? [];
  const contratos = emenda.contratos ?? [];
  const contratoComProcesso = contratos.find(c => c.instrumento === 'TERMO_FOMENTO' && c.processoCompraNumero)
    ?? contratos.find(c => c.processoCompraNumero);
  const processoDeCompra = contratoComProcesso
    ? numeroComAno(contratoComProcesso.processoCompraNumero, contratoComProcesso.processoCompraAno)
    : null;
  const atosLegais = emenda.atosLegais ?? [];
  const dadosIncompletos = !emenda.objectDetail || !emenda.institutionName || emenda.value == null;
  const nomeTipo = tiposEmenda.find(item => item.codigo === emenda.classification)?.nome ?? emendaClassificationLabel(emenda.classification);
  const pago = emenda.valorPago ?? 0;
  const percentualPago = formatPercent(pago, emenda.value);
  const larguraBarra = emenda.value ? Math.min(100, Math.max(0, (pago / emenda.value) * 100)) : 0;

  const temContratos = contratos.length > 0 || atosLegais.length > 0 || !!processoDeCompra;
  const temEvidencias = evidencias.length > 0 || anexosPrestacao.length > 0;
  const temMetasBens = execucaoPorMeta.length > 0 || alcanceMetas.length > 0 || bens.length > 0;

  // Seções na ordem em que aparecem; o menu segue a mesma ordem e só lista o que existe.
  const secoes: [string, string, boolean][] = [
    ['identificacao', 'Identificação', true],
    ['execucao', 'Execução financeira', true],
    ['planejamento', 'Plano de trabalho', true],
    ['contratacoes', 'Contratos', temContratos],
    ['prestacao-contas', 'Prestação de contas', true],
    ['despesas', 'Despesas', despesas.length > 0],
    ['metas-bens', 'Metas e bens', temMetasBens],
    ['evidencias', 'Evidências', temEvidencias],
    ['documentos', 'Documentos', documentos.length > 0],
  ];
  const naoPublicados = [
    !temContratos && 'contratos ou Termo de Fomento',
    despesas.length === 0 && 'despesas comprovadas',
    !temEvidencias && 'evidências da execução',
    documentos.length === 0 && 'documentos públicos',
  ].filter(Boolean) as string[];

  const tabela = (children: React.ReactNode, minWidth: string, caption: string) => (
    <div className="mt-4 overflow-x-auto">
      <table className={`w-full divide-y text-sm ${minWidth} ${tema.divider}`}>
        <caption className="sr-only">{caption}</caption>
        {children}
      </table>
    </div>
  );

  return (
    <PortalAcessibilidadeShell tema={tema} barra={barra}>
      <header className={tema.header}>
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <Link to="/transparencia" className={`inline-flex items-center gap-2 text-sm font-semibold ${tema.headerText}`}><ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />Voltar à consulta pública</Link>
          <p className={`mt-5 text-xs font-semibold uppercase tracking-[0.18em] ${tema.headerKicker}`}>Portal da Transparência · emenda parlamentar</p>
          <h1 ref={titleRef} tabIndex={-1} className="mt-1 text-2xl font-bold outline-none focus-visible:ring-2 focus-visible:ring-white">
            Emenda {identificacao.numero}/{identificacao.exercicio}
          </h1>
          <p className={`mt-1 text-sm ${tema.headerText}`}>
            {[emenda.officialCode && `Código ${emenda.officialCode}`, nomeTipo, emenda.esfera].filter(Boolean).join(' · ')}
          </p>
        </div>
      </header>

      <main id="conteudo-principal" className="mx-auto w-full max-w-7xl flex-1 space-y-5 px-4 py-6 sm:px-6 lg:px-8">
        <section aria-labelledby="titulo-resumo" className={`rounded-xl p-5 ${tema.card}`}>
          <h2 id="titulo-resumo" className="sr-only">Resumo da emenda</h2>
          <div className="flex flex-wrap items-center gap-3">
            <span className={dt}>Situação</span>
            <SituacaoBadge status={emenda.status} alto={tema.alto} />
          </div>
          <p className="mt-3 text-lg font-semibold leading-snug">{emenda.objectDetail || 'Objeto não informado'}</p>
          <dl className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div><dt className={dt}>Parlamentar</dt><dd className="mt-1 font-medium">{emenda.councilorName || 'Não informado'}</dd></div>
            <div className="lg:col-span-2"><dt className={dt}>Beneficiário</dt><dd className="mt-1 font-medium">{emenda.institutionName || 'Não informado'}{emenda.beneficiaryLocality && <span className={`block text-sm font-normal ${tema.muted}`}>{emenda.beneficiaryLocality}</span>}</dd></div>
            <div><dt className={dt}>Valor previsto</dt><dd className="mt-1 text-lg font-bold">{formatCurrency(emenda.value)}</dd></div>
            <div><dt className={dt}>Pago</dt><dd className="mt-1 text-lg font-bold">{formatCurrency(pago)}</dd>{percentualPago && <dd className={`text-xs ${tema.muted}`}>{percentualPago} do previsto</dd>}</div>
          </dl>
          {emenda.value ? (
            <div className="mt-4">
              <div className={`h-2 w-full overflow-hidden rounded-full ${tema.alto ? 'bg-gray-800' : 'bg-slate-200 dark:bg-slate-700'}`} role="img" aria-label={`Pago ${percentualPago ?? '0%'} do valor previsto`}>
                <div className={`h-full rounded-full ${tema.alto ? 'bg-yellow-400' : 'bg-emerald-600 dark:bg-emerald-500'}`} style={{ width: `${larguraBarra}%` }} />
              </div>
              <p className={`mt-1 text-xs ${tema.muted}`}>Saldo a executar: {formatCurrency(emenda.saldoExecutar)}</p>
            </div>
          ) : null}
        </section>

        <section aria-label="Atualização dos dados" className={`rounded-xl p-4 text-sm ${tema.card}`}>
          {metadataIndisponivel ? (
            <p className="font-medium text-amber-800 dark:text-amber-300" role="status">Metadados de período e atualização indisponíveis no momento.</p>
          ) : (
            <div className="flex flex-wrap justify-between gap-3">
              <p><span className="font-semibold">Período coberto:</span> {metadata?.primeiroExercicio ?? '—'} a {metadata?.ultimoExercicio ?? '—'}</p>
              <p><span className="font-semibold">Última atualização:</span> {formatDateTime(metadata?.ultimaAtualizacao)}</p>
              {metadata?.responsavel && <ResponsavelTransparencia responsavel={metadata.responsavel} />}
            </div>
          )}
        </section>

        <nav aria-label="Seções da emenda" className={`overflow-x-auto rounded-xl p-3 ${tema.card}`}>
          <ul className={`flex min-w-max gap-2 text-sm font-semibold ${tema.link}`}>
            {secoes.filter(([, , existe]) => existe).map(([id, label]) => (
              <li key={id}><a href={`#${id}`} className="block rounded-md px-3 py-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-700">{label}</a></li>
            ))}
          </ul>
        </nav>

        {dadosIncompletos && <p role="status" className={`rounded-xl p-4 text-sm ${tema.aviso}`}><strong>Informação incompleta:</strong> um ou mais dados de identificação, objeto ou valor ainda não foram informados pelo órgão responsável.</p>}
        {naoPublicados.length > 0 && (
          <p className={`rounded-xl border border-dashed p-4 text-sm ${tema.divider} ${tema.muted}`}>
            <span className="font-semibold">Ainda sem publicação para esta emenda:</span> {naoPublicados.join(', ')}.
          </p>
        )}

        <section id="identificacao" aria-labelledby="titulo-identificacao" className={card}>
          <h2 id="titulo-identificacao" className="text-lg font-bold">Identificação e alocação</h2>
          <dl className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ['Número da emenda', identificacao.numero],
              ['Exercício', identificacao.exercicio],
              ['Código oficial', emenda.officialCode],
              ['Origem', emenda.esfera],
              ['Tipo', nomeTipo],
              ['Órgão executor', emenda.executingAgency],
              ['Função de governo', formatFuncaoGoverno(emenda.funcaoCodigo)],
              ['Forma de repasse', emenda.tipoTransferencia],
            ].map(([label, value]) => <div key={label}><dt className={dt}>{label}</dt><dd className="mt-1 font-medium">{value || 'Não informado'}</dd></div>)}
          </dl>
          <h3 className="mt-6 text-sm font-bold">Vínculo orçamentário</h3>
          <dl className="mt-3 grid gap-4 sm:grid-cols-3">
            <div><dt className={dt}>Tipo do ato</dt><dd className="mt-1 font-medium">{ATO_ORCAMENTARIO_LABEL[emenda.atoNormativoTipo ?? ''] ?? formatEnum(emenda.atoNormativoTipo)}</dd></div>
            <div><dt className={dt}>Número</dt><dd className="mt-1 font-medium">{numeroComAno(emenda.atoNormativoNumero, emenda.atoNormativoAno)}</dd></div>
            <div><dt className={dt}>Publicação oficial</dt><dd className="mt-1 font-medium">{emenda.atoNormativoUrl ? <a href={emenda.atoNormativoUrl} target="_blank" rel="noopener noreferrer" className={`underline ${tema.link}`}>Consultar ato orçamentário</a> : 'Não informado'}</dd></div>
          </dl>
        </section>

        <section id="execucao" aria-labelledby="titulo-execucao" className={card}>
          <h2 id="titulo-execucao" className="text-lg font-bold">Execução orçamentária e financeira</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ['Empenhado', emenda.valorEmpenhado],
              ['Liquidado', emenda.valorLiquidado],
              ['Pago', emenda.valorPago],
              ['Saldo a executar', emenda.saldoExecutar],
            ].map(([label, value]) => <div key={String(label)} className={`rounded-lg p-4 ${tema.inner}`}><p className={dt}>{label}</p><p className="mt-1 text-lg font-bold">{formatCurrency(value as number | undefined)}</p></div>)}
          </div>
          <dl className={`mt-4 grid gap-4 rounded-lg p-4 sm:grid-cols-3 ${tema.soft}`}>
            <div><dt className={dt}>Situação da execução</dt><dd className="mt-1 font-medium">{formatEnum(emenda.situacaoExecucao)}</dd></div>
            <div><dt className={dt}>Data de referência</dt><dd className="mt-1 font-medium">{formatDate(emenda.dataReferenciaExecucao)}</dd></div>
            <div><dt className={dt}>Última atualização</dt><dd className="mt-1 font-medium">{emenda.ultimaAtualizacaoExecucao ? formatDateTime(emenda.ultimaAtualizacaoExecucao) : 'Não informado'}</dd></div>
          </dl>
          {movimentos.length === 0 ? (
            <p className={vazio}>Ainda não há empenho, liquidação ou pagamento registrado para esta emenda.</p>
          ) : (
            <>
              <ul className={`mt-4 divide-y rounded-lg sm:hidden ${tema.inner} ${tema.divider}`}>
                {movimentos.map((movimento, index) => (
                  <li key={`${movimento.numeroDocumento}-${index}`} className="p-3 text-sm">
                    <div className="flex justify-between gap-2"><span className="font-semibold">{formatEnum(movimento.tipo)}{movimento.natureza === 'ANULACAO' ? ' (anulação)' : ''}</span><span className="font-bold">{formatCurrency(movimento.valor)}</span></div>
                    <p className={`text-xs ${tema.muted}`}>{movimento.numeroDocumento || 'Documento não informado'} · {formatDate(movimento.dataMovimento)}</p>
                    <p className="mt-1 text-xs">{movimento.credor || 'Credor não informado'}{movimento.descricaoDespesa ? ` · ${movimento.descricaoDespesa}` : ''}</p>
                  </li>
                ))}
              </ul>
              <div className="hidden sm:block">
                {tabela(<>
                  <thead className={tema.tableHead}><tr>{['Etapa', 'Documento', 'Data', 'Credor', 'Descrição'].map(item => <th key={item} scope="col" className={th}>{item}</th>)}<th scope="col" className={`${th} text-right`}>Valor</th></tr></thead>
                  <tbody className={`divide-y ${tema.divider}`}>{movimentos.map((movimento, index) => <tr key={`${movimento.numeroDocumento}-${index}`}><td className="px-3 py-3">{formatEnum(movimento.tipo)}{movimento.natureza === 'ANULACAO' ? ' (anulação)' : ''}</td><td className="px-3 py-3">{movimento.numeroDocumento || 'Não informado'}</td><td className="px-3 py-3">{formatDate(movimento.dataMovimento)}</td><td className="px-3 py-3">{movimento.credor || 'Não informado'}</td><td className="px-3 py-3">{movimento.descricaoDespesa || 'Não informado'}</td><td className="whitespace-nowrap px-3 py-3 text-right font-medium">{formatCurrency(movimento.valor)}</td></tr>)}</tbody>
                </>, 'min-w-[640px]', 'Movimentos de empenho, liquidação e pagamento')}
              </div>
            </>
          )}
        </section>

        <section id="planejamento" aria-labelledby="titulo-planejamento" className={card}>
          <h2 id="titulo-planejamento" className="text-lg font-bold">Plano de trabalho</h2>
          <dl className="mt-4 grid gap-4 sm:grid-cols-3">
            <div><dt className={dt}>Instrumento</dt><dd className="mt-1 font-medium">{instrumento}</dd></div>
            <div><dt className={dt}>Início previsto</dt><dd className="mt-1 font-medium">{formatDate(emenda.periodoInicio)}</dd></div>
            <div><dt className={dt}>Término previsto</dt><dd className="mt-1 font-medium">{formatDate(emenda.periodoFim)}</dd></div>
          </dl>
          {!plano ? (
            <p className={`mt-4 rounded-lg p-4 text-sm ${tema.aviso}`}><strong>Ainda não publicado:</strong> o plano de trabalho só aparece aqui depois de aprovado.</p>
          ) : (
            <div className={`mt-5 space-y-5 border-t pt-5 ${tema.divider}`}>
              <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div><dt className={dt}>Título</dt><dd className="mt-1 font-medium">{plano.titulo || 'Não informado'}</dd></div>
                <div><dt className={dt}>Modalidade</dt><dd className="mt-1 font-medium">{formatEnum(plano.modalidade)}</dd></div>
                <div><dt className={dt}>Valor</dt><dd className="mt-1 font-medium">{formatCurrency(plano.valor)}</dd></div>
                <div><dt className={dt}>Aprovação</dt><dd className="mt-1 font-medium">{formatDate(plano.aprovadoEm)}</dd></div>
              </dl>
              {plano.contaEspecifica && (
                <div className={`rounded-lg p-4 ${tema.inner} ${tema.soft}`}>
                  <h3 className="font-bold">Conta bancária específica da parceria</h3>
                  <dl className="mt-3 grid gap-3 sm:grid-cols-3">
                    <div><dt className={dt}>Banco</dt><dd className="mt-1 font-medium">{plano.contaEspecifica.banco}</dd></div>
                    <div><dt className={dt}>Agência</dt><dd className="mt-1 font-medium">{plano.contaEspecifica.agencia}</dd></div>
                    <div><dt className={dt}>Conta / identificador</dt><dd className="mt-1 font-medium">{plano.contaEspecifica.conta}</dd></div>
                  </dl>
                </div>
              )}
              {[['Objeto da parceria', plano.objetoParceria], ['Realidade diagnosticada', plano.descricaoRealidade], ['Forma de execução', plano.formaExecucao]].map(([titulo, texto]) => (
                <section key={titulo} aria-label={titulo}>
                  <h3 className={dt}>{titulo}</h3>
                  <p className="mt-2 max-w-4xl whitespace-pre-line leading-7">{texto || 'Não informado'}</p>
                </section>
              ))}
              <div className={`border-t pt-4 ${tema.divider}`}>
                <h3 className="font-bold">Metas, etapas e cronograma</h3>
                {(plano.metas ?? []).length === 0 ? (
                  <p className={`mt-3 text-sm ${tema.muted}`}>Nenhuma meta registrada no plano.</p>
                ) : (
                  <div className="mt-3 space-y-4">
                    {(plano.metas ?? []).map((meta, metaIndex) => (
                      <article key={`${meta.ordem}-${metaIndex}`} className={`rounded-lg p-4 ${tema.inner}`}>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div><p className={`text-xs font-semibold uppercase ${tema.link}`}>Meta {metaIndex + 1}</p><h4 className="mt-1 font-bold">{meta.titulo || 'Sem título'}</h4><p className={`mt-1 text-sm ${tema.muted}`}>{meta.descricao || 'Sem descrição'}</p></div>
                          <p className="font-bold">{formatCurrency(meta.valor)}</p>
                        </div>
                        {(meta.itens ?? []).length > 0 && tabela(<>
                          <thead className={tema.tableHead}><tr>{['Etapa', 'Tipo', 'Quantidade', 'Período', 'Valor'].map(item => <th key={item} scope="col" className={th}>{item}</th>)}</tr></thead>
                          <tbody className={`divide-y ${tema.divider}`}>{(meta.itens ?? []).map((item, itemIndex) => <tr key={`${item.titulo}-${itemIndex}`}><td className="px-3 py-3"><span className="font-medium">{item.titulo || `Etapa ${itemIndex + 1}`}</span><span className={`block text-xs ${tema.muted}`}>{item.descricao}</span></td><td className="px-3 py-3">{formatEnum(item.tipo)}</td><td className="px-3 py-3">{item.quantidade ?? 'Não informado'} {item.unidade || ''}</td><td className="px-3 py-3">{formatDate(item.periodoInicio)} a {formatDate(item.periodoFim)}</td><td className="px-3 py-3 font-medium">{formatCurrency(item.valor)}</td></tr>)}</tbody>
                        </>, 'min-w-[640px]', `Etapas da meta ${metaIndex + 1}`)}
                        {(meta.cronograma ?? []).length > 0 && (
                          <div className="mt-4"><p className={dt}>Cronograma</p><ul className="mt-2 space-y-2">{(meta.cronograma ?? []).map((evento, eventoIndex) => <li key={`${evento.dataPrevista}-${eventoIndex}`} className="flex gap-3 text-sm"><time className="font-semibold">{formatDate(evento.dataPrevista)}</time><span>{evento.atividade || 'Atividade não informada'}</span></li>)}</ul></div>
                        )}
                      </article>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        {temContratos && (
          <section id="contratacoes" aria-labelledby="titulo-contratacoes" className={card}>
            <h2 id="titulo-contratacoes" className="text-lg font-bold">Contratos e atos legais</h2>
            <p className={`mt-1 text-sm ${tema.muted}`}>Contratos, Termo de Fomento, processo de compra e atos legais vindos do sistema administrativo da prefeitura.</p>
            <div className="mt-4"><p className={dt}>Processo de compra</p><p className="mt-1 font-medium">{processoDeCompra ?? 'Não informado'}</p></div>
            {contratos.length > 0 && (
              <div className="mt-4 grid gap-3 lg:grid-cols-2">
                {contratos.map((contrato, index) => (
                  <article key={`${contrato.numero}-${contrato.ano}-${index}`} className={`rounded-lg p-4 ${tema.inner}`}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <p className="font-semibold">{formatEnum(contrato.instrumento)} {numeroComAno(contrato.numero, contrato.ano)}</p>
                      <p className="whitespace-nowrap font-bold">{formatCurrency(contrato.valor)}</p>
                    </div>
                    <p className={`mt-1 text-xs ${tema.muted}`}>Vigência: {formatDate(contrato.vigenciaInicio)} a {formatDate(contrato.vigenciaFim)}</p>
                    {contrato.processoCompraNumero && <p className={`mt-0.5 text-xs ${tema.muted}`}>Processo de compra: {numeroComAno(contrato.processoCompraNumero, contrato.processoCompraAno)}</p>}
                    <p className="mt-2 break-words text-xs leading-5">{contrato.objeto || 'Objeto não informado'}</p>
                  </article>
                ))}
              </div>
            )}
            {atosLegais.length > 0 && (
              <div className={`mt-5 border-t pt-4 ${tema.divider}`}>
                <p className={dt}>Atos legais da dotação</p>
                <ul className="mt-3 space-y-2">
                  {atosLegais.map((ato, index) => (
                    <li key={`${ato.numero}-${ato.ano}-${index}`} className={`rounded-lg p-3 text-sm ${tema.inner}`}>
                      <p className="font-semibold">Ato legal nº {numeroComAno(ato.numero, ato.ano)}{ato.dataRevogacao ? ` (revogado em ${formatDate(ato.dataRevogacao)})` : ''}</p>
                      {ato.ementa && <p className="mt-1 text-xs leading-5">{ato.ementa}</p>}
                      <p className={`mt-1 text-xs ${tema.muted}`}>Publicado em {formatDate(ato.dataPublicacao)}{ato.linkOficial && <> · <a href={ato.linkOficial} target="_blank" rel="noopener noreferrer" className={`font-semibold underline ${tema.link}`}>Texto oficial</a></>}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        <section id="prestacao-contas" aria-labelledby="titulo-prestacao-contas" className={card}>
          <h2 id="titulo-prestacao-contas" className="text-lg font-bold">Prestação de contas</h2>
          {!prestacaoContas ? (
            <p className={vazio}>A prestação de contas ainda não começou: o plano de trabalho não está em execução.</p>
          ) : (
            <div className="mt-4 space-y-4">
              <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div><dt className={dt}>Situação</dt><dd className="mt-1 font-medium">{PRESTACAO_SITUACAO_LABEL[prestacaoContas.situacao ?? ''] ?? formatEnum(prestacaoContas.situacao)}</dd></div>
                <div><dt className={dt}>Prazo final</dt><dd className="mt-1 font-medium">{formatDate(prestacaoContas.prazoFinal)}</dd></div>
                <div><dt className={dt}>Última atualização</dt><dd className="mt-1 font-medium">{formatDateTime(prestacaoContas.ultimaAtualizacao)}</dd></div>
                <div><dt className={dt}>Concluída em</dt><dd className="mt-1 font-medium">{prestacaoContas.situacao === 'CONCLUIDA' ? formatDateTime(prestacaoContas.concluidaEm) : 'Em aberto'}</dd></div>
              </dl>
              {(prestacaoContas.prestacoes ?? []).length > 0 && tabela(<>
                <thead className={tema.tableHead}><tr>{['Prestação', 'Status', 'Prazo', 'Atualizada em'].map(item => <th key={item} scope="col" className={th}>{item}</th>)}</tr></thead>
                <tbody className={`divide-y ${tema.divider}`}>{(prestacaoContas.prestacoes ?? []).map((pc, index) => (
                  <tr key={`${pc.protocolo}-${index}`}>
                    <td className="px-3 py-3"><span className="font-medium">PC {pc.numero}{pc.tipo === 'UNICA' ? ' (única)' : ''}</span>{pc.protocolo && <span className={`block text-xs ${tema.muted}`}>{pc.protocolo}</span>}</td>
                    <td className="px-3 py-3">{PRESTACAO_PC_STATUS_LABEL[pc.status ?? ''] ?? formatEnum(pc.status)}</td>
                    <td className="px-3 py-3">{formatDate(pc.prazoFinal)}{pc.prorrogada && <span className={`block text-xs ${tema.muted}`}>{pc.prazoOriginal ? `Prorrogado (original ${formatDate(pc.prazoOriginal)})` : 'Prorrogado'}</span>}</td>
                    <td className="px-3 py-3">{formatDateTime(pc.atualizadaEm)}</td>
                  </tr>
                ))}</tbody>
              </>, 'min-w-[560px]', 'Prestações de contas parciais e final')}
            </div>
          )}
        </section>

        {despesas.length > 0 && (
          <section id="despesas" aria-labelledby="titulo-despesas" className={card}>
            <h2 id="titulo-despesas" className="text-lg font-bold">Despesas comprovadas</h2>
            <p className={`mt-1 text-sm ${tema.muted}`}>Despesas das prestações de contas já encerradas, sem pendência de ajuste.</p>
            {tabela(<>
              <thead className={tema.tableHead}><tr>{['Prestação', 'Credor', 'Documento fiscal', 'Pagamento', 'Fonte'].map(item => <th key={item} scope="col" className={th}>{item}</th>)}<th scope="col" className={`${th} text-right`}>Valor</th></tr></thead>
              <tbody className={`divide-y ${tema.divider}`}>{despesas.map((despesa, index) => (
                <tr key={`${despesa.prestacaoProtocolo}-${despesa.numeroDocumentoFiscal}-${index}`}>
                  <td className="px-3 py-3">{despesa.prestacaoProtocolo || (despesa.prestacaoNumero ? `PC ${despesa.prestacaoNumero}` : 'Não informado')}</td>
                  <td className="px-3 py-3"><span className="font-medium">{despesa.credor || 'Não informado'}</span>{despesa.credorCnpj && <span className={`block text-xs ${tema.muted}`}>CNPJ {despesa.credorCnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')}</span>}</td>
                  <td className="px-3 py-3">{[despesa.tipoDocumentoFiscal, despesa.numeroDocumentoFiscal].filter(Boolean).join(' nº ') || 'Não informado'}<span className={`block text-xs ${tema.muted}`}>{formatDate(despesa.dataDocumentoFiscal)}</span></td>
                  <td className="px-3 py-3">{formatDate(despesa.dataPagamento)}</td>
                  <td className="px-3 py-3">{despesa.fonteRecurso || 'Não informado'}</td>
                  <td className="whitespace-nowrap px-3 py-3 text-right font-medium">{formatCurrency(despesa.valor)}</td>
                </tr>
              ))}</tbody>
            </>, 'min-w-[720px]', 'Despesas comprovadas na prestação de contas')}
          </section>
        )}

        {temMetasBens && (
          <section id="metas-bens" aria-labelledby="titulo-metas-bens" className={card}>
            <h2 id="titulo-metas-bens" className="text-lg font-bold">Execução das metas e bens adquiridos</h2>
            <p className={`mt-1 text-sm ${tema.muted}`}>Dados das prestações de contas já encerradas.</p>
            {execucaoPorMeta.length > 0 && tabela(<>
              <thead className={tema.tableHead}><tr>{['Meta', 'Planejado', 'Executado'].map(item => <th key={item} scope="col" className={th}>{item}</th>)}</tr></thead>
              <tbody className={`divide-y ${tema.divider}`}>{execucaoPorMeta.map((linha, index) => (
                <tr key={`${linha.meta}-${index}`}><td className="px-3 py-3 font-medium">{linha.meta || 'Meta sem título'}</td><td className="px-3 py-3">{formatCurrency(linha.valorPlanejado)}</td><td className="px-3 py-3">{formatCurrency(linha.valorExecutado)}</td></tr>
              ))}</tbody>
            </>, 'min-w-[480px]', 'Valor executado por meta')}
            {alcanceMetas.length > 0 && tabela(<>
              <thead className={tema.tableHead}><tr>{['Meta', 'Quantidade realizada', 'Alcance', 'Como foi aferido'].map(item => <th key={item} scope="col" className={th}>{item}</th>)}</tr></thead>
              <tbody className={`divide-y ${tema.divider}`}>{alcanceMetas.map((linha, index) => (
                <tr key={`${linha.meta}-${index}`}><td className="px-3 py-3 font-medium">{linha.meta || 'Meta sem título'}</td><td className="px-3 py-3">{linha.quantidadeRealizada ?? 'Não informado'}</td><td className="px-3 py-3">{linha.percentualAlcance != null ? `${linha.percentualAlcance}%` : 'Não informado'}</td><td className="px-3 py-3">{linha.meioAfericao || 'Não informado'}</td></tr>
              ))}</tbody>
            </>, 'min-w-[560px]', 'Alcance das metas')}
            {bens.length > 0 && tabela(<>
              <thead className={tema.tableHead}><tr>{['Bem', 'Quantidade', 'Valor total', 'Documento', 'Situação'].map(item => <th key={item} scope="col" className={th}>{item}</th>)}</tr></thead>
              <tbody className={`divide-y ${tema.divider}`}>{bens.map((bem, index) => (
                <tr key={`${bem.especificacao}-${index}`}><td className="px-3 py-3 font-medium">{bem.especificacao || 'Não informado'}</td><td className="px-3 py-3">{bem.quantidade ?? 'Não informado'}</td><td className="px-3 py-3">{formatCurrency(bem.valorTotal)}</td><td className="px-3 py-3">{[bem.documentoTipo, bem.documentoNumero].filter(Boolean).join(' nº ') || 'Não informado'}<span className={`block text-xs ${tema.muted}`}>{formatDate(bem.dataDocumento)}</span></td><td className="px-3 py-3">{formatEnum(bem.situacao)}</td></tr>
              ))}</tbody>
            </>, 'min-w-[640px]', 'Bens adquiridos')}
          </section>
        )}

        {temEvidencias && (
          <section id="evidencias" aria-labelledby="titulo-evidencias" className={card}>
            <h2 id="titulo-evidencias" className="text-lg font-bold">Evidências da execução</h2>
            <p className={`mt-1 text-sm ${tema.muted}`}>Arquivos revisados pela prefeitura antes da publicação, sem dados pessoais desnecessários.</p>
            <ul className={`mt-4 divide-y rounded-lg ${tema.inner} ${tema.divider}`}>
              {evidencias.map((evidencia, index) => (
                <li key={`${evidencia.categoria}-${evidencia.url}-${index}`} className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div>
                    <p className={`text-xs font-semibold uppercase ${tema.link}`}>{formatEnum(evidencia.categoria)}</p>
                    <p className="mt-1 font-semibold">{evidencia.descricao || evidencia.nome || 'Evidência de execução'}</p>
                    <p className={`mt-1 text-xs ${tema.muted}`}>Documento de {formatDate(evidencia.dataDocumento)}{evidencia.nome ? ` · ${evidencia.nome}` : ''}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <a href={transparenciaService.resolveDocumentUrl(evidencia.url)} target="_blank" rel="noopener noreferrer" className={botaoSecundario}>Visualizar</a>
                    {evidencia.downloadUrl && <a href={transparenciaService.resolveDocumentUrl(evidencia.downloadUrl)} download className={botaoPrimario}>Baixar</a>}
                  </div>
                </li>
              ))}
              {anexosPrestacao.map((anexo, index) => (
                <li key={`${anexo.url}-${index}`} className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div>
                    <p className={`text-xs font-semibold uppercase ${tema.link}`}>Prestação de contas</p>
                    <p className="mt-1 font-semibold">{anexo.descricao || anexo.nome || 'Anexo da prestação de contas'}</p>
                    <p className={`mt-1 text-xs ${tema.muted}`}>{anexo.nome}{anexo.revisadoEm ? ` · revisado em ${formatDateTime(anexo.revisadoEm)}` : ''}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <a href={transparenciaService.resolveDocumentUrl(anexo.url)} target="_blank" rel="noopener noreferrer" className={botaoSecundario}>Visualizar</a>
                    {anexo.downloadUrl && <a href={transparenciaService.resolveDocumentUrl(anexo.downloadUrl)} download className={botaoPrimario}>Baixar</a>}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {documentos.length > 0 && (
          <section id="documentos" aria-labelledby="titulo-documentos" className={card}>
            <h2 id="titulo-documentos" className="text-lg font-bold">Instrumentos e documentos públicos</h2>
            <ul className={`mt-4 divide-y rounded-lg ${tema.inner} ${tema.divider}`}>
              {documentos.map((documento, index) => (
                <li key={`${documento.tipo}-${index}`} className="flex flex-wrap items-center justify-between gap-3 p-4">
                  <div><p className="font-semibold">{documento.nome || formatEnum(documento.tipo)}</p><p className={`mt-1 text-xs ${tema.muted}`}>{documento.descricao || formatEnum(documento.tipo)}{documento.dataPublicacao ? ` · Publicado em ${formatDate(documento.dataPublicacao)}` : ''}</p></div>
                  <div className="flex flex-wrap gap-2">
                    <a href={transparenciaService.resolveDocumentUrl(documento.url)} target="_blank" rel="noopener noreferrer" className={botaoSecundario}>Visualizar</a>
                    {documento.downloadUrl && <a href={transparenciaService.resolveDocumentUrl(documento.downloadUrl)} download={documento.nome || true} className={botaoPrimario}>{isPlanoAprovado(documento.tipo) ? 'Baixar plano aprovado' : 'Baixar'}</a>}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </PortalAcessibilidadeShell>
  );
};

export default TransparenciaEmendaPage;
